import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2,
  Check,
  X,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { cn } from '../../lib/utils';
import { apiClient } from '../../lib/api-client';

export interface ProposedStep {
  id: string;
  action: 'create' | 'delete' | 'update';
  resourceType: string;
  resourceName: string;
  reason: string;
  optional: boolean;
  defaultSelected: boolean;
}

/** Rows from GET .../reconciliation/audit (live existence vs YAML). */
export interface AuditResourceRow {
  resourceType: string;
  resourceName: string;
  roleCode: string;
  status: string;
  existsExternally?: boolean;
  configuredInYaml?: boolean;
}

export interface ReconciliationPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  proposedSteps: ProposedStep[];
  mode: 'edit' | 'audit';
  executePayload: {
    streamId: string;
    entityType: 'role' | 'stream';
    entityId: string;
    patch?: any;
    yaml?: string;
  };
  /** When set (stream reconcile), shown as read-only status before catch-up steps. */
  auditRows?: AuditResourceRow[];
  onExecuted: (results: Array<{ name: string; status: string; error?: string }>) => void;
  onConfigOnlySave?: () => void;
}

function formatResourceTypeLabel(resourceType: string): string {
  return resourceType
    .split('_')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}

function actionBadgeVariant(action: ProposedStep['action']): 'success' | 'destructive' | 'warning' {
  if (action === 'create') return 'success';
  if (action === 'delete') return 'destructive';
  return 'warning';
}

function actionLabel(action: ProposedStep['action']): string {
  return action.charAt(0).toUpperCase() + action.slice(1);
}

function auditStatusLabel(status: string): string {
  switch (status) {
    case 'in_sync':
      return 'Reconciled';
    case 'missing_external':
      return 'Missing externally';
    case 'orphaned':
      return 'Orphaned';
    case 'config_only':
      return 'Config only';
    default:
      return status;
  }
}

function auditStatusBadgeVariant(
  status: string,
): 'success' | 'destructive' | 'warning' | 'secondary' | 'outline' {
  if (status === 'in_sync') return 'success';
  if (status === 'missing_external') return 'warning';
  if (status === 'orphaned') return 'destructive';
  return 'outline';
}

export function ReconciliationPreviewDialog({
  open,
  onClose,
  proposedSteps,
  mode,
  executePayload,
  auditRows,
  onExecuted,
  onConfigOnlySave,
}: ReconciliationPreviewDialogProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [collapsedTypes, setCollapsedTypes] = useState<Set<string>>(new Set());
  const [executing, setExecuting] = useState(false);
  const [executeError, setExecuteError] = useState<string | null>(null);
  const [executionResults, setExecutionResults] = useState<
    Array<{ name: string; status: string; error?: string }> | null
  >(null);

  const stepsKey = useMemo(
    () => proposedSteps.map((s) => `${s.id}:${s.defaultSelected ? 1 : 0}`).join('|'),
    [proposedSteps],
  );

  useEffect(() => {
    if (!open) return;
    const next = new Set<string>();
    for (const s of proposedSteps) {
      if (s.defaultSelected) next.add(s.id);
    }
    setSelected(next);
    setCollapsedTypes(new Set());
    setExecuting(false);
    setExecuteError(null);
    setExecutionResults(null);
  }, [open, stepsKey]);

  const grouped = useMemo(() => {
    const map = new Map<string, ProposedStep[]>();
    for (const step of proposedSteps) {
      const list = map.get(step.resourceType) ?? [];
      list.push(step);
      map.set(step.resourceType, list);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [proposedSteps]);

  const toggleTypeCollapsed = useCallback((type: string) => {
    setCollapsedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  const toggleStep = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const interactionLocked = executing || executionResults !== null;

  const handleProceed = async () => {
    setExecuteError(null);
    setExecuting(true);
    try {
      const approvedStepIds = Array.from(selected);
      const res = await apiClient.executeReconciliation({
        ...executePayload,
        approvedStepIds,
      });
      const steps = res.steps ?? [];
      const results = steps.map((s: { name: string; status: string; error?: string }) => ({
        name: s.name,
        status: s.status,
        error: s.error,
      }));
      setExecutionResults(results);
      onExecuted(results);
    } catch (e: any) {
      const message = e?.message || 'Execution failed';
      setExecuteError(message);
    } finally {
      setExecuting(false);
    }
  };

  const title =
    mode === 'audit' ? 'Reconcile external resources' : 'Review external resource changes';
  const subtitle =
    mode === 'audit'
      ? 'Live checks cover GitHub repo, ADO project, Teams team name, and vault secret for bots. Other resources may only appear in catch-up steps. Select provisioning steps to run; reconciled rows are informational.'
      : 'Choose which proposed changes to apply. Config may already be saved; this runs side effects only for selected steps.';

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent maxWidth="3xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 pr-8">{subtitle}</p>
        </DialogHeader>

        <DialogBody>
          {executeError && (
            <div className="mb-4 flex gap-2 rounded-md bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300">
              <AlertTriangle className="w-5 h-5 shrink-0" aria-hidden />
              <span>{executeError}</span>
            </div>
          )}

          {mode === 'audit' && auditRows && auditRows.length > 0 && (
            <div className="mb-6 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-3 py-2 text-sm font-semibold text-gray-800 dark:text-gray-100 bg-gray-50 dark:bg-gray-800/80">
                Live resource status ({auditRows.length})
              </div>
              <ul className="divide-y divide-gray-100 dark:divide-gray-800 max-h-56 overflow-y-auto">
                {auditRows.map((row, i) => (
                  <li key={`${row.roleCode}-${row.resourceType}-${row.resourceName}-${i}`} className="px-3 py-2.5 flex flex-wrap items-center gap-2 text-sm">
                    <Badge variant={auditStatusBadgeVariant(row.status)}>{auditStatusLabel(row.status)}</Badge>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{formatResourceTypeLabel(row.resourceType)}</span>
                    <span className="text-gray-600 dark:text-gray-400 break-all">{row.resourceName}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-500 font-mono">role {row.roleCode}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {mode === 'audit' && executePayload.entityType === 'stream' && !executePayload.yaml && (
            <div className="mb-4 flex gap-2 rounded-md bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-900 dark:text-amber-200">
              <AlertTriangle className="w-5 h-5 shrink-0" aria-hidden />
              <span>Stream YAML was not loaded; Proceed cannot run provisioning. Close and open reconcile again.</span>
            </div>
          )}

          {proposedSteps.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No catch-up provisioning steps. Nothing to execute.</p>
          ) : (
            <div className="space-y-4">
              {mode === 'audit' && (
                <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Provisioning actions (select to run)
                </p>
              )}
              {grouped.map(([resourceType, steps]) => {
                const collapsed = collapsedTypes.has(resourceType);
                return (
                  <div
                    key={resourceType}
                    className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() => toggleTypeCollapsed(resourceType)}
                      className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-semibold text-gray-800 dark:text-gray-100 bg-gray-50 dark:bg-gray-800/80 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      {collapsed ? (
                        <ChevronRight className="w-4 h-4 shrink-0 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-4 h-4 shrink-0 text-gray-500" />
                      )}
                      {formatResourceTypeLabel(resourceType)}
                      <span className="ml-auto text-xs font-normal text-gray-500 dark:text-gray-400">
                        {steps.length} {steps.length === 1 ? 'item' : 'items'}
                      </span>
                    </button>

                    {!collapsed && (
                      <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                        {steps.map((step) => {
                          const checked = selected.has(step.id);
                          return (
                            <li
                              key={step.id}
                              className={cn(
                                'flex gap-3 px-3 py-3',
                                step.optional && 'opacity-70',
                              )}
                            >
                              <label className="flex min-w-0 flex-1 cursor-pointer gap-3">
                                <input
                                  type="checkbox"
                                  className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900"
                                  checked={checked}
                                  disabled={interactionLocked}
                                  onChange={() => toggleStep(step.id)}
                                />
                                <span className="min-w-0 flex-1 space-y-1">
                                  <span className="flex flex-wrap items-center gap-2">
                                    <Badge variant={actionBadgeVariant(step.action)}>
                                      {actionLabel(step.action)}
                                    </Badge>
                                    <span className="font-medium text-gray-900 dark:text-gray-100 break-all">
                                      {step.resourceName}
                                    </span>
                                    {step.optional && (
                                      <Badge variant="secondary" className="font-normal">
                                        Optional
                                      </Badge>
                                    )}
                                  </span>
                                  <span className="block text-sm text-gray-600 dark:text-gray-400">
                                    {step.reason}
                                  </span>
                                </span>
                              </label>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {executionResults && executionResults.length > 0 && (
            <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                Execution results
              </h3>
              <ul className="space-y-2">
                {executionResults.map((row, i) => {
                  const ok = row.status === 'completed';
                  return (
                    <li
                      key={`${row.name}-${i}`}
                      className="flex gap-2 text-sm text-gray-800 dark:text-gray-200"
                    >
                      {ok ? (
                        <Check className="w-4 h-4 shrink-0 text-green-600 dark:text-green-400 mt-0.5" />
                      ) : (
                        <X className="w-4 h-4 shrink-0 text-red-600 dark:text-red-400 mt-0.5" />
                      )}
                      <span className="min-w-0">
                        <span className="font-medium">{row.name}</span>
                        {!ok && row.error && (
                          <span className="block text-xs text-red-600 dark:text-red-400 mt-0.5">
                            {row.error}
                          </span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {executionResults && executionResults.length === 0 && (
            <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4 text-sm text-gray-500 dark:text-gray-400">
              No operations were run (no steps selected or nothing to execute).
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={executing}>
            Cancel
          </Button>
          {mode === 'edit' && onConfigOnlySave && (
            <Button
              variant="outline"
              onClick={onConfigOnlySave}
              disabled={executing || executionResults !== null}
            >
              Save Config Only
            </Button>
          )}
          <Button
            variant="default"
            onClick={handleProceed}
            disabled={
              executing ||
              executionResults !== null ||
              (mode === 'audit' &&
                executePayload.entityType === 'stream' &&
                !executePayload.yaml) ||
              (mode === 'audit' && proposedSteps.length === 0)
            }
          >
            {executing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden />
                Running…
              </>
            ) : (
              'Proceed'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
